export const TRADE_IN_SHIPPING_OPTIONS = ['PRINT_LABEL', 'DROP_OFF', 'FREE_KIT'] as const
export type TradeInShippingOption = typeof TRADE_IN_SHIPPING_OPTIONS[number]

export const TRADE_IN_RETURN_METHODS = ['SHIP_TO_CUSTOMER', 'PICKUP_AT_STORE'] as const
export type TradeInReturnMethod = typeof TRADE_IN_RETURN_METHODS[number]

export interface TradeInLocation {
  id: string
  code?: string | null
  name: string
  recipientName?: string | null
  addressLine1: string
  addressLine2?: string | null
  city: string
  state?: string | null
  postalCode: string
  countryCode: string
  phone?: string | null
  email?: string | null
  businessHours?: Record<string, unknown> | null
  shippingInstructions?: string | null
  instructions?: string | Record<string, unknown> | null
  appointmentRequired?: boolean | null
  returnPickupAvailable?: boolean | null
  operationalStatus?: 'OPEN' | 'PAUSED' | 'CLOSED' | null
  effectiveNotice?: Record<string, unknown> | null
  shippingBlocked?: boolean | null
  replacementAssignment?: TradeInLocation | null
  contentVersion?: string | number | null
}

export interface TradeInDeliverySelection {
  shippingOption: TradeInShippingOption
  dropOffLocationId?: string
  returnMethod?: TradeInReturnMethod | null
  returnAddressId?: string
}

export interface TradeInDeliveryPayload {
  shippingOption: TradeInShippingOption
  dropOffLocationId?: string
  returnMethod?: TradeInReturnMethod
  returnAddressId?: string
  /** Compatibility field for clients and backends during the v1 migration window. */
  shippingAddressId?: string
}

export interface TradeInActionSource {
  status: string
  shippingOption?: string | null
  trackingNumber?: string | null
  needsCustomerAction?: boolean | null
  returnStatus?: string | null
  nextCustomerActionCode?: string | null
}

export interface TradeInAllowedActionSource {
  allowedActions?: string[] | null
}

/** Server capabilities are authoritative; absent and empty lists both fail closed. */
export function hasTradeInAllowedAction(source: TradeInAllowedActionSource | null | undefined, action: string): boolean {
  return Array.isArray(source?.allowedActions) && source.allowedActions.includes(action)
}

export interface TradeInWizardDraftInput {
  currentStep: number
  selectedBrand: unknown
  selectedCategory: unknown
  selectedProduct: unknown
  selectedPropertyValueIds: string[]
  skipDetailedInspection: boolean
  selectedShippingOption: TradeInShippingOption
  selectedReturnAddressId: string
  selectedReturnMethod: TradeInReturnMethod | null
  selectedDropOffLocationId: string
  selectedPaymentMethod: string
}

export interface TradeInWizardDraft extends TradeInWizardDraftInput {
  schemaVersion: 2
  createdAt: number
  expiresAt: number
}

export interface TradeInOperationStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export function buildTradeInCreateOperationPayload<
  T extends { selectedPropertyValueIds: string[] },
>(request: T): T {
  return {
    ...request,
    // Match the backend's canonical create hash so harmless selection ordering
    // cannot change the operation identity.
    selectedPropertyValueIds: [...request.selectedPropertyValueIds].sort(),
  }
}

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
const WEEKDAY_ALIASES: Readonly<Record<string, typeof WEEKDAYS[number]>> = {
  mon: 'mon', monday: 'mon',
  tue: 'tue', tuesday: 'tue',
  wed: 'wed', wednesday: 'wed',
  thu: 'thu', thursday: 'thu',
  fri: 'fri', friday: 'fri',
  sat: 'sat', saturday: 'sat',
  sun: 'sun', sunday: 'sun',
}

export function normalizeTradeInShippingOption(value: unknown): TradeInShippingOption {
  if (typeof value === 'string' && TRADE_IN_SHIPPING_OPTIONS.includes(value as TradeInShippingOption)) {
    return value as TradeInShippingOption
  }
  throw new Error(`Unsupported trade-in shipping option: ${String(value)}`)
}

export function normalizeTradeInReturnMethod(value: unknown): TradeInReturnMethod {
  if (typeof value === 'string' && TRADE_IN_RETURN_METHODS.includes(value as TradeInReturnMethod)) {
    return value as TradeInReturnMethod
  }
  throw new Error(`Unsupported trade-in return method: ${String(value)}`)
}

export function buildTradeInDeliveryPayload(selection: TradeInDeliverySelection): TradeInDeliveryPayload {
  const shippingOption = normalizeTradeInShippingOption(selection.shippingOption)
  const returnMethod = selection.returnMethod == null
    ? undefined
    : normalizeTradeInReturnMethod(selection.returnMethod)

  if (shippingOption === 'FREE_KIT') {
    throw new Error('FREE_KIT is read-only for historical trade-in orders')
  }

  if (shippingOption === 'PRINT_LABEL') {
    if (returnMethod === 'PICKUP_AT_STORE') throw new Error('Store pickup is unavailable for self-ship orders')
    if (returnMethod === 'SHIP_TO_CUSTOMER' && !selection.returnAddressId) {
      throw new Error('A return address is required for postal returns')
    }
  }

  if (shippingOption === 'DROP_OFF') {
    if (!selection.dropOffLocationId) throw new Error('A drop-off location is required')
    if (returnMethod === 'SHIP_TO_CUSTOMER' && !selection.returnAddressId) {
      throw new Error('A return address is required for postal returns')
    }
  }

  const payload: TradeInDeliveryPayload = { shippingOption }
  if (returnMethod) payload.returnMethod = returnMethod
  if (shippingOption === 'DROP_OFF') payload.dropOffLocationId = selection.dropOffLocationId
  if (selection.returnAddressId && (shippingOption === 'PRINT_LABEL' || returnMethod === 'SHIP_TO_CUSTOMER')) {
    payload.returnAddressId = selection.returnAddressId
    payload.shippingAddressId = selection.returnAddressId
  }
  return payload
}

/** Keep an explicitly saved order destination only while it still belongs to the customer. */
export function resolveSavedReturnAddressId(
  preferredAddressId: string | null | undefined,
  addresses: ReadonlyArray<{ id: string }>,
): string {
  return preferredAddressId && addresses.some(address => address.id === preferredAddressId)
    ? preferredAddressId
    : ''
}

export function shouldShowInboundTracking(order: TradeInActionSource): boolean {
  return order.shippingOption === 'PRINT_LABEL'
}

export function getTradeInNextAction(order: TradeInActionSource):
  'MAIL_DEVICE' | 'DROP_OFF_DEVICE' | 'REVIEW_ADJUSTED_PRICE' | 'CONFIRM_RETURN_PREFERENCE' |
  'PICK_UP_DEVICE' | 'CONTACT_SUPPORT_RETURN_EXCEPTION' | null {
  const supported = new Set([
    'MAIL_DEVICE', 'DROP_OFF_DEVICE', 'REVIEW_ADJUSTED_PRICE', 'CONFIRM_RETURN_PREFERENCE',
    'PICK_UP_DEVICE', 'CONTACT_SUPPORT_RETURN_EXCEPTION',
  ])
  return supported.has(order.nextCustomerActionCode || '')
    ? order.nextCustomerActionCode as 'MAIL_DEVICE' | 'DROP_OFF_DEVICE' |
      'REVIEW_ADJUSTED_PRICE' | 'CONFIRM_RETURN_PREFERENCE' | 'PICK_UP_DEVICE' |
      'CONTACT_SUPPORT_RETURN_EXCEPTION'
    : null
}

function postalCountryName(countryCode: string): string {
  const code = countryCode.trim().toUpperCase()
  if (!code) return ''
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code)?.toLocaleUpperCase('en') || code
  } catch {
    return code
  }
}

export function formatTradeInLocationStreetAddress(location: TradeInLocation): string {
  const city = location.city?.trim()
  const state = location.state?.trim()
  const locality = [location.postalCode?.trim(), city].filter(Boolean).join(' ')
  const localityWithRegion = state && state.toLocaleLowerCase() !== city?.toLocaleLowerCase()
    ? [locality, state].filter(Boolean).join(', ')
    : locality

  return [
    location.addressLine1?.trim(),
    location.addressLine2?.trim(),
    localityWithRegion,
    postalCountryName(location.countryCode),
  ].filter(value => Boolean(value)).join('\n')
}

/** Postal-label order: organisation, recipient, delivery point, locality, destination country. */
export function formatTradeInLocationAddress(location: TradeInLocation, includeLocationName = false): string {
  return [
    includeLocationName ? location.name?.trim() : '',
    location.recipientName?.trim(),
    formatTradeInLocationStreetAddress(location),
  ].filter(value => Boolean(value)).join('\n')
}

export function orderedTradeInBusinessHours(hours?: Record<string, unknown> | null): Array<[string, string]> {
  if (!hours) return []
  const normalized = new Map<string, string>()
  const unknown: Array<[string, string]> = []
  for (const [rawDay, rawValue] of Object.entries(hours)) {
    const value = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '')
    const day = WEEKDAY_ALIASES[rawDay.toLowerCase()]
    if (day) normalized.set(day, value)
    else unknown.push([rawDay, value])
  }
  return [
    ...WEEKDAYS.flatMap(day => normalized.has(day) ? [[day, normalized.get(day)!] as [string, string]] : []),
    ...unknown.sort(([a], [b]) => a.localeCompare(b)),
  ]
}

export function createTradeInWizardDraft(
  input: TradeInWizardDraftInput,
  now = Date.now(),
  ttlMs = 2 * 60 * 60 * 1000,
): TradeInWizardDraft {
  return { schemaVersion: 2, createdAt: now, expiresAt: now + ttlMs, ...input }
}

export function parseTradeInWizardDraft(raw: string | null, now = Date.now()): TradeInWizardDraft | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<TradeInWizardDraft>
    if (value.schemaVersion !== 2 || typeof value.expiresAt !== 'number' || value.expiresAt <= now) return null
    normalizeTradeInShippingOption(value.selectedShippingOption)
    if (value.selectedReturnMethod != null) normalizeTradeInReturnMethod(value.selectedReturnMethod)
    if (!Number.isInteger(value.currentStep) || value.currentStep! < 1 || value.currentStep! > 5) return null
    if (!Array.isArray(value.selectedPropertyValueIds)) return null
    return value as TradeInWizardDraft
  } catch {
    return null
  }
}

export function getSafeTradeInRestoreStep(currentStep: number): number {
  // Step 5 contains the final submission action, but payout credentials are
  // intentionally memory-only and cannot survive a reload.
  return currentStep === 5 ? 4 : currentStep
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, child]) => [key, canonicalize(child)]))
  }
  return value
}

async function requestFingerprint(payload: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(canonicalize(payload)))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('')
}

function newOperationKey(): string {
  const random = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : [...crypto.getRandomValues(new Uint8Array(16))].map(value => value.toString(16).padStart(2, '0')).join('')
  // Backend enforces a 64-character maximum. Scope stays in the storage key;
  // the transmitted key only needs globally unique entropy.
  return `ti:${random}`
}

export async function getStableTradeInOperationKey(
  scope: string,
  payload: unknown,
  storage: TradeInOperationStorage = sessionStorage,
): Promise<string> {
  const storageKey = `midigi_trade_in_operation:${scope}`
  const fingerprint = await requestFingerprint(payload)
  try {
    const saved = JSON.parse(storage.getItem(storageKey) || 'null') as { key?: unknown, fingerprint?: unknown } | null
    if (saved && typeof saved.key === 'string' && saved.fingerprint === fingerprint) return saved.key
  } catch { /* Invalid operation state is replaced below. */ }
  const key = newOperationKey()
  storage.setItem(storageKey, JSON.stringify({ key, fingerprint }))
  return key
}

export function clearStableTradeInOperationKey(
  scope: string,
  storage: TradeInOperationStorage = sessionStorage,
): void {
  storage.removeItem(`midigi_trade_in_operation:${scope}`)
}
